import classNames from "classnames";
import ActivityPreviewLink from "PFApp/components/activity_commons/activity_preview_link";
import Tooltip from "PFComponents/tooltip/tooltip";
import { useRoleName } from "PFCore/helpers/activities";
import { useReadableAvailability } from "PFCore/helpers/use_readable_availability";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import Dot from "./dot";
import css from "./progress_bar.less";
import Step from "./step";

const ProgressBar = ({ role, engagement }) => {
  const isDraft = (activity) => activity.state === "draft";
  const { formatDate } = useDateFormatter();
  const { t } = useTranslation("workflow", { keyPrefix: "parts.engagements" });
  const getRoleName = useRoleName();
  const { readableAvailability } = useReadableAvailability({ availability: role?.duration });

  const dotsData = useMemo(() => {
    const baseData = [
      {
        level: 0,
        action: null,
        title: isDraft(engagement)
          ? t("progressBar.engagementDraftCreated")
          : t("progressBar.engagementCreated"),
        key: "engagement_created",
        created_at: engagement.created_at
      },
      {
        level: 1,
        action: "activity_created",
        title: t("progressBar.roleCreated"),
        key: "created"
      },
      {
        level: 2,
        action: "shortlist_created",
        title: t("progressBar.shortlisting"),
        key: "shortlisting"
      },
      {
        level: 3,
        action: "shortlist_assigned",
        title: t("translation:invited"),
        key: "invited"
      },
      {
        level: 4,
        action: "shortlist_filled",
        title: t("progressBar.roleFilled"),
        key: "filled"
      }
    ];

    if (role) {
      return baseData.map((dot) => {
        const auditLog = role.audit_logs.filter((log) => log.action === dot.action)[0];
        return { ...dot, ...auditLog };
      });
    } else {
      return baseData;
    }
  }, [t]);

  const level = (dotsData.filter((dot) => dot.created_at).slice(-1)[0] || {}).level || 0;

  const activity = role || engagement;
  const isOverdue = !isDraft(activity) && new Date().getTime() > new Date(activity.post_until).getTime();

  const backgroundOffset = level === 0 ? 0 : `calc(${level * 25}% - 10px)`;
  const progressOffset = `calc(${level * 25}% + 8px)`;

  const roleSharedAt =
    role &&
    (
      role.audit_logs
        .filter((log) => log.action === "owner_created" && log.target_profile_id !== role.profile_id)
        .pop() || {}
    ).created_at;

  return (
    <div className={css.root}>
      {role && (
        <div className={css.top}>
          <div className={css.description}>
            <div>
              {isOverdue && (
                <span style={{ marginRight: 5 }} className={classNames(css.label, css.overdue)}>
                  {t("progressBar.overdue")}
                </span>
              )}
              <strong>
                <ActivityPreviewLink
                  activity={role}
                  className={css.roleLink}
                  qaId={`WorkflowPageProgressRoleLink.${role.id}`}
                >
                  {role && isDraft(role)
                    ? t("progressBar.roleNameDraft", { roleName: getRoleName(role) })
                    : getRoleName(role)}
                </ActivityPreviewLink>
              </strong>
            </div>
            {roleSharedAt && (
              <span style={{ marginTop: 4 }}>{t("sharedAtDate", { date: formatDate(roleSharedAt) })}</span>
            )}
          </div>
          {readableAvailability && (
            <div className={css.duration}>
              <Tooltip
                content={t("progressBar.availabilityRequirement", { roleDuration: readableAvailability })}
                className={css.infoTooltip}
                placement="top-end"
                theme="pf-info"
              >
                <span>{readableAvailability}</span>
              </Tooltip>
            </div>
          )}
        </div>
      )}
      <div className={css.box}>
        <div className={css.trackWrap}>
          <div
            className={classNames(css.trackBg, { [css.trackBgOverdue]: isOverdue })}
            style={{ left: backgroundOffset }}
          />
          {level !== 0 && (
            <div
              className={classNames(css.trackDone, css[dotsData[level].key])}
              style={{ width: progressOffset }}
            />
          )}
        </div>
        <div className={css.dots}>
          {dotsData.map((data) => (
            <Dot key={data.key} dotLevel={data.level} currentLevel={level} isOverdue={isOverdue} />
          ))}
        </div>
      </div>
      <div className={css.bottom}>
        {dotsData.map((data) => (
          <Step key={data.key} data={data} currentLevel={level} role={role} engagement={engagement} />
        ))}
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.number.isRequired,
    state: PropTypes.string.isRequired,
    duration: PropTypes.object,
    title: PropTypes.string.isRequired,
    post_until: PropTypes.string,
    profile_id: PropTypes.number,
    audit_logs: PropTypes.arrayOf(
      PropTypes.shape({
        action: PropTypes.string.isRequired,
        created_at: PropTypes.string.isRequired,
        requester_id: PropTypes.number.isRequired,
        target_profile_id: PropTypes.number
      })
    )
  }),
  engagement: PropTypes.shape({
    state: PropTypes.string.isRequired,
    created_at: PropTypes.string.isRequired,
    post_until: PropTypes.string,
    duration: PropTypes.object
  }).isRequired
};

export default ProgressBar;
